import { createApp as createClientApp } from 'vue'

import { createHead } from '@unhead/vue'
import { InferSeoMetaPlugin } from '@unhead/addons'
import { createPinia } from 'pinia'
import { createRouter } from './router'
import VueroApp from './VueroApp.vue'
import VueGtag from 'vue-gtag'
import DraggableResizableVue from 'draggable-resizable-vue3'
import { vMouseWheel } from '/@src/utils/report/ScrollHandlerDirective';
import { authCheck } from '/@src/utils/auth'

// import { AVPlugin } from "vue-audio-visual";
import './styles'

import * as Sentry from '@sentry/vue'

export type VueroAppContext = Awaited<ReturnType<typeof createApp>>
export type VueroPlugin = (vuero: VueroAppContext) => void | Promise<void>

const plugins = import.meta.glob<{ default: VueroPlugin }>('./plugins/*.ts')

// this is a helper function to define plugins with autocompletion
export function definePlugin(plugin: VueroPlugin) {
  return plugin
}

export async function createApp() {
  const app = createClientApp(VueroApp)
  const router = createRouter()
  authCheck()

  const head = createHead({
    plugins: [InferSeoMetaPlugin()],
  })
  app.use(head)

  const pinia = createPinia()
  app.use(pinia)

  const vuero = {
    app,
    router,
    head,
    pinia,
  }

  app.provide('vuero', vuero)

  for (const path in plugins) {
    try {
      const { default: plugin } = await plugins[path]()
      await plugin(vuero)
    }
    catch (error) {
      console.error(`Error while loading plugin "${path}".`)
      console.error(error)
    }
  }

  if (import.meta.env.VITE_GOOGLE_ANALYTICS_ID) {
    app.use(
      VueGtag,
      {
        config: {
          id: import.meta.env.VITE_GOOGLE_ANALYTICS_ID,
        },
      },
      router
    )
  }

  Sentry.init({
    app,
    dsn: 'https://7e7daac240554985b8c68ff70ace1690@o4509132789186560.ingest.us.sentry.io/4509134017724416',
    environment: import.meta.env.VITE_API_BASE_ENV,
    beforeSend(event, hint) {
      // Prevent sending events from localhost
      if (import.meta.env.VITE_API_BASE_ENV.toLowerCase() != "staging" || import.meta.env.VITE_API_BASE_ENV.toLowerCase() != "production") {
        return null; // Drop the event
      }
      return event;
    },
    integrations: [
      Sentry.replayIntegration(),
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: 0.2,
    tracePropagationTargets: ['localhost',"https://apps.kazee.ai","https://kazee.ai","https://apis.kazee.ai","https://api.kazee.ai"],

    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 0.1, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  })

  // use router after plugin registration, so we can register navigation guards
  app.use(vuero.router)

  // app.use(AVPlugin);
  app.directive('mouse-wheel', vMouseWheel);

  app.use(DraggableResizableVue)

  return vuero
}
